import React, { useState, useEffect, useRef } from 'react';
import OlcButton from '~/components/General/OlcButton';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Hooks
import useScreenWidth from '~/hooks/useScreenWidth';
import useSortParams from '~/hooks/useSortParams';
import useListUrlQuery from '~/hooks/useListUrlQuery';

// Utils
import { dateFormat, formatCallDuration } from '~/utils/date-format';
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION } from '~/utils/constants';
import { formatPhoneNumber } from '~/utils/helperFunctions';
import {
  PHONE_CALL_HISTORY_SORT_KEYS,
  PHONE_SMS_HISTORY_SORT_KEYS,
  PHONE_TRACKING_SORT_KEYS,
} from '~/utils/listQueryParams';

// MUI Components
import {
  Box,
  debounce,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Typography,
  GlobalStyles,
} from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcTablePagination from '~/components/General/OlcTablePagination';

// Icons
import AddIcon from '@mui/icons-material/Add';
import {
  ContactSearchCIcon,
  HistoryCIcon,
  EditPhoneCIcon,
  ReleasePhoneCIcon,
  ReleasePhoneNumberCIcon,
  CustomFieldIcon,
} from '~/assets/images';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';

// Redux Actions
import {
  fetchPhoneTrackingNumbers,
  fetchPhoneTrackingHistory,
  fetchSmsTrackingHistory,
  releasePhoneTrackingNumber,
} from '~/redux/actions/phone-tracking-actions';
import { getAllPaymentMethods } from '~/redux/actions/stripe-actions';
import { failure } from '~/redux/actions/snackbar-actions';

// Components
import ActivateTrackingModal from '~/components/PhoneTracking/ActivateTrackingModal';
import LinkedTemplatesModal from '~/components/PhoneTracking/LinkedTemplatesModal';
import MultiSelect from '~/components/Contacts/AdvanceFilters/MultiSelect';
import ConfirmModal from '~/components/General/ConfirmModal';
import NoTrackingScreen from '~/components/PhoneTracking/NoTrackingScreen';
import NoRecords from '~/components/General/NoRecords';
import PaymentMethod from '~/components/Account/Wallet/PaymentMethod';
import Stripe from '~/components/Stripe/Stripe';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';
import OlcActionMenu from '~/components/General/OlcActionMenu';

// Styles
import './styles.scss';

const classes = {
  row: 'phoneTrackingDataGridRow',
  cell: 'phoneTrackingDataGridCell',
  cellContent: 'phoneTrackingCellContent',
  virtualScroller: 'phoneTrackingVirtualScroller',
};

const PhoneTracking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: trackingId, type: historyType } = useParams();
  const isWideScreen = useScreenWidth(1600);
  const isMediumScreen = useScreenWidth(1400);
  const isCompanyEnabled = false;
  const pageSizes = DEFAULT_PAGE_SIZES;

  // Redux State
  const phoneTrackingNumbers = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingNumbers
  );
  const totalCount = useSelector((state) => state.phoneTrackingReducers.rowsCount);
  const totalRecordsInDB = useSelector((state) => state.phoneTrackingReducers.totalRecordsInDB);
  const loading = useSelector((state) => state.phoneTrackingReducers.loading);
  const perPage = useSelector(
    (state) => state.phoneTrackingReducers.perPage || DEFAULT_PAGINATION.pageSize
  );
  const lastPage = useSelector((state) => state.phoneTrackingReducers.lastPage);
  const from = useSelector((state) => state.phoneTrackingReducers.from);
  const to = useSelector((state) => state.phoneTrackingReducers.to);
  const currentPage = useSelector((state) => state.phoneTrackingReducers.currentPage);
  const phoneTrackingHistory = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingHistory
  );
  const historyCount = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingHistoryCount
  );
  const historyFrom = useSelector((state) => state.phoneTrackingReducers.phoneTrackingHistoryFrom);
  const historyTo = useSelector((state) => state.phoneTrackingReducers.phoneTrackingHistoryTo);
  const historyLastPage = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingHistoryLastPage
  );
  const historyCurrentPage = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingHistoryCurrentPage
  );
  const historyPerPage = useSelector(
    (state) =>
      state.phoneTrackingReducers.phoneTrackingHistoryPerPage || DEFAULT_PAGINATION.pageSize
  );
  const historyLoader = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingHistoryLoader
  );
  const phoneTrackingSmsHistory = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistory
  );
  const smsHistoryCount = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryCount
  );
  const smsHistoryFrom = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryFrom
  );
  const smsHistoryTo = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryTo
  );
  const smsHistoryLastPage = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryLastPage
  );
  const smsHistoryCurrentPage = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryCurrentPage
  );
  const smsHistoryPerPage = useSelector(
    (state) =>
      state.phoneTrackingReducers.phoneTrackingSmsHistoryPerPage || DEFAULT_PAGINATION.pageSize
  );
  const smsHistoryLoader = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingSmsHistoryLoader
  );
  const paymentMethods = useSelector((state) => state.stripeReducers?.methods || []);
  const isTestMode = useSelector((state) => state.userReducers.user.isLiveMode === false);

  // Local State
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const showHistory = Boolean(trackingId);
  const isSmsHistory = historyType === 'sms';
  const historyRequestKey = showHistory ? `${trackingId}:${historyType || 'call'}` : null;
  const [loadedHistoryRequestKey, setLoadedHistoryRequestKey] = useState(null);
  // Search and column sort live in the URL (the source of truth), so they survive reload and
  // back/forward navigation. `search` below is only the text currently typed in the input.
  // The list and the call / SMS history views each have their own sort keys; the history views
  // only use the sort (they have no search).
  let sortKeys = PHONE_TRACKING_SORT_KEYS;
  if (showHistory) {
    sortKeys = isSmsHistory ? PHONE_SMS_HISTORY_SORT_KEYS : PHONE_CALL_HISTORY_SORT_KEYS;
  }
  const {
    queryString,
    parsedQuery,
    appliedSearch,
    sort,
    updateUrl: updatePhoneTrackingUrl,
  } = useListUrlQuery(sortKeys);
  const searchApplied = Boolean(appliedSearch);

  const [search, setSearch] = useState(appliedSearch);
  const location = useLocation();

  const perPageRef = useRef(perPage);
  perPageRef.current = perPage;
  const hasLoadedListRef = useRef(false);
  const [modalOpen, setModalOpen] = useState({
    type: '',
    open: false,
  });
  const [linkedTemplatesModal, setLinkedTemplatesModal] = useState({
    open: false,
    templates: [],
  });
  const [paymentMethodModal, setPaymentMethodModal] = useState(false);
  const [wasPaymentMethodModalOpen, setWasPaymentMethodModalOpen] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    description: '',
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });

  // Fetches a page of the list for the current URL (applied search + sort).
  const loadPhoneNumbers = (page = currentPage, pageSize = perPage) =>
    dispatch(
      fetchPhoneTrackingNumbers({
        pagination: { page, pageSize },
        search: appliedSearch,
        ...sort,
      })
    );

  // Applying the same search again just refreshes the list.
  const handleSearch = () => {
    const term = search.trim();

    if (term && !updatePhoneTrackingUrl({ search: term })) {
      loadPhoneNumbers(1);
    }
  };

  // Emptying the input removes an applied search from the URL (the list then shows all numbers).
  const handleSearchChange = (event) => {
    const nextSearch = event.target.value.trimStart();

    setSearch(nextSearch);

    if (!nextSearch && appliedSearch) {
      updatePhoneTrackingUrl({ search: '' });
    }
  };

  const clearSearch = () => {
    setSearch('');
    updatePhoneTrackingUrl({ search: '' });
  };

  // Navigate to call or SMS history — URL drives the view
  const handleViewHistory = (phoneTrackingId, type = 'call') => {
    // Remember the list's search/sort so "Back to Tracking Phone Numbers" can restore them.
    navigate(`/phone-tracking/${phoneTrackingId}/${type}`, { state: { listQuery: queryString } });
  };

  const handleHistoryActionKeyDown = (event, phoneTrackingId, type) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      handleViewHistory(phoneTrackingId, type);
    }
  };

  // Fetch history data whenever URL params change (covers direct navigation & reload, with the
  // sort from the URL). A sort change by the user is fetched by the sort handler below instead.
  useEffect(() => {
    let isActive = true;

    const loadHistory = async () => {
      if (!trackingId) return;

      if (historyType === 'sms') {
        await dispatch(fetchSmsTrackingHistory({ id: trackingId, ...sort }));
      } else {
        await dispatch(fetchPhoneTrackingHistory({ id: trackingId, ...sort }));
      }

      if (isActive) {
        setLoadedHistoryRequestKey(historyRequestKey);
      }
    };

    loadHistory();

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingId, historyType]);

  // Handle phone number release
  const handleReleasePhoneNumber = async () => {
    if (selectedRow) {
      try {
        setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
        const response = await dispatch(releasePhoneTrackingNumber(selectedRow.id));

        if (response.status === 200) {
          // Refresh the list after successful release
          loadPhoneNumbers();

          setOpenConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
        } else {
          const linkedTemplates = response?.data?.data?.templates;
          if (linkedTemplates?.length > 0) {
            setOpenConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
            setLinkedTemplatesModal({ open: true, templates: linkedTemplates });
          } else {
            dispatch(
              failure(
                response?.data?.message || 'Failed to release the phone number. Please try again.'
              )
            );
          }
        }
      } catch (error) {
        console.error('Error releasing phone number:', error);
        setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
      } finally {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  const handleOpenConfirmModal = () => {
    setOpenConfirmModal({
      type: 'release-phone-number',
      open: true,
      message:
        'Once released, you will no longer own the number and future calls will not be forwarded. Historical data and analytics will remain available. ',
      description: 'This action cannot be undone.',
      handleOk: handleReleasePhoneNumber,
      loading: false,
      header: 'Release Tracking Phone Number',
      successButtonName: 'Yes',
      icon: ReleasePhoneNumberCIcon,
      svg: '',
      handleClose: () => {
        setOpenConfirmModal((prev) => ({ ...prev, open: false }));
      },
    });
  };

  // Column space: Phone Name shrinks together with the other text columns (priority 1) instead of
  // first, so it keeps a readable width; the date columns shrink last so their dates stay complete.
  const columns = [
    {
      field: 'name',
      headerName: 'Phone Name',
      width: 200,
      flex: isWideScreen ? 1 : 0,
      compactMaxWidth: 200,
      compactShrinkMinWidth: 80,
      compactShrinkPriority: 1,
      sortable: true,
      renderCell: (params) => {
        return (
          <Tooltip
            title={params.row.name}
            placement="bottom"
            disableHoverListener={params.row.name.length < 18}
          >
            <Typography className="phoneTrackingCellContent">{params.row.name}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'phoneNumber',
      headerName: 'Tracking Number',
      width: 250,
      flex: isWideScreen ? 1 : 0,
      compactShrinkMinWidth: 105,
      compactShrinkPriority: 1,
      sortable: true,
      renderCell: (params) => {
        return (
          <Typography className="phoneTrackingCellContent">
            {formatPhoneNumber(params.row.phoneNumber)}
          </Typography>
        );
      },
    },
    {
      field: 'forwardingPhoneNumber',
      headerName: 'Call Forwarding Number',
      width: 250,
      flex: isWideScreen ? 1.2 : 0,
      compactShrinkMinWidth: 105,
      compactShrinkPriority: 1,
      sortable: true,
      renderCell: (params) => {
        return (
          <Typography className="phoneTrackingCellContent">
            {formatPhoneNumber(params.row.forwardingPhoneNumber)}
          </Typography>
        );
      },
    },
    {
      field: 'smsForwardingEmail',
      headerName: 'SMS Forwarding Email',
      width: 350,
      flex: isWideScreen ? 1.5 : 0,
      compactShrinkMinWidth: 110,
      compactShrinkPriority: 0,
      sortable: false,
      renderCell: (params) => {
        const email = params.row.smsForwardingEmail;
        return email ? (
          <Tooltip title={email} placement="bottom">
            <Typography className="phoneTrackingCellContent">{email}</Typography>
          </Tooltip>
        ) : (
          <Typography className="phoneTrackingCellContent">-</Typography>
        );
      },
    },
    {
      field: 'callCount',
      headerName: 'Calls',
      width: 100,
      flex: isWideScreen ? 0.5 : 0,
      compactShrinkMinWidth: 55,
      compactShrinkPriority: 1,
      sortable: true,
      sortKey: 'numberOfCalls',
      renderCell: (params) => {
        const callCount = params.row.numberOfCalls ?? 0;

        // Only a count above 0 links to the history; there is nothing to open for 0.
        if (!(callCount > 0)) {
          return <Typography className="phoneTrackingCellContent">{callCount}</Typography>;
        }

        return (
          <Typography
            className="phoneTrackingCellContent phoneTrackingCountAction"
            onClick={(event) => {
              event.stopPropagation();
              handleViewHistory(params.row.id, 'call');
            }}
            onKeyDown={(event) => handleHistoryActionKeyDown(event, params.row.id, 'call')}
            role="link"
            tabIndex={0}
            title="View call history"
          >
            {callCount}
          </Typography>
        );
      },
    },
    {
      field: 'smsCount',
      headerName: 'SMS',
      width: 100,
      flex: isWideScreen ? 0.5 : 0,
      compactShrinkMinWidth: 55,
      compactShrinkPriority: 1,
      sortable: true,
      sortKey: 'numberOfSms',
      renderCell: (params) => {
        const smsCount = params.row.numberOfSms ?? 0;

        // Only a count above 0 links to the history; there is nothing to open for 0.
        if (!(smsCount > 0)) {
          return <Typography className="phoneTrackingCellContent">{smsCount}</Typography>;
        }

        return (
          <Typography
            className="phoneTrackingCellContent phoneTrackingCountAction"
            onClick={(event) => {
              event.stopPropagation();
              handleViewHistory(params.row.id, 'sms');
            }}
            onKeyDown={(event) => handleHistoryActionKeyDown(event, params.row.id, 'sms')}
            role="link"
            tabIndex={0}
            title="View SMS history"
          >
            {smsCount}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 90,
      flex: isWideScreen ? 0.6 : 0,
      compactShrinkMinWidth: 70,
      compactShrinkPriority: 1,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.row.status} placement="bottom">
          <Typography className="phoneTrackingCellContent">{params.row.status}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'lastReceivedAt',
      headerName: 'Last Received',
      width: 190,
      compactWidth: 210,
      flex: isWideScreen ? 0.8 : 0,
      compactShrinkMinWidth: 150,
      compactShrinkPriority: 2,
      sortable: true,
      renderCell: (params) => (
        <Typography className="phoneTrackingCellContent">
          {dateFormat(params.row.lastReceivedAt) || '-'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Activated On',
      width: 120,
      compactWidth: 140,
      flex: isWideScreen ? 0.8 : 0,
      compactShrinkMinWidth: 132,
      compactShrinkPriority: 1,
      sortable: true,
      renderCell: (params) => (
        <Typography className="phoneTrackingCellContent">
          {dateFormat(params.row.createdAt, true)}
        </Typography>
      ),
    },
    {
      field: 'Actions',
      headerName: '',
      width: 50,
      flex: isWideScreen ? 0.3 : 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            <IconButton
              id={`phone-tracking-actions-${params.row.id}`}
              aria-controls={
                params.row.id === selectedRow?.id && Boolean(anchorEl)
                  ? 'phone-tracking-actions-menu'
                  : undefined
              }
              aria-haspopup="menu"
              aria-expanded={
                params.row.id === selectedRow?.id && Boolean(anchorEl) ? 'true' : undefined
              }
              aria-label={`Actions for ${params.row.name}`}
              onClick={(event) => {
                event.stopPropagation();
                setAnchorEl(event.currentTarget);
                setSelectedRow(params.row);
              }}
              sx={{
                opacity: params.row.id === selectedRow?.id && Boolean(anchorEl) ? '0.8' : '0.6',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        );
      },
    },
  ];

  const rows = phoneTrackingNumbers || [];

  const numberDetailsRows =
    phoneTrackingHistory?.length > 0
      ? phoneTrackingHistory.map((call) => ({
          id: call.id,
          date: dateFormat(call.createdAt, false),
          CallFrom: formatPhoneNumber(call.callFrom),
          CallTo: formatPhoneNumber(call.callTo),
          CallDuration: formatCallDuration(call.callDuration),
          Status: call.status || '',
        }))
      : [];

  const numberDetailsColumns = [
    {
      field: 'date',
      sortKey: 'createdAt',
      headerName: 'Date',
      width: 250,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: true,
    },
    {
      field: 'CallFrom',
      sortKey: 'callFrom',
      headerName: 'Call From',
      width: 200,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: true,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography>{params.row.CallFrom || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'CallTo',
      sortKey: 'callTo',
      headerName: 'Call To',
      width: 200,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: true,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography>{params.row.CallTo || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'CallDuration',
      sortKey: 'callDuration',
      headerName: 'Call Duration',
      width: 150,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: true,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography>{params.row.CallDuration || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'Status',
      sortKey: 'status',
      headerName: 'Status',
      width: 150,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: true,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography sx={{ textTransform: 'capitalize' }}>{params.row.Status || '-'}</Typography>
          </Box>
        );
      },
    },
  ];

  // A new sort goes to the URL; the URL effect below resets to page 1 and refetches.
  const { sortProps } = useSortParams(
    columns,
    (nextSort) => updatePhoneTrackingUrl({ sort: nextSort }),
    sort
  );

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const debouncedHandleKeyDown = debounce(searchKeyDown, 300);

  const handleChangePage = (event, newPage) => {
    loadPhoneNumbers(newPage);
  };

  const smsDetailsRows =
    phoneTrackingSmsHistory?.length > 0
      ? phoneTrackingSmsHistory.map((sms) => ({
          id: sms.id,
          date: dateFormat(sms.createdAt, false),
          SmsFrom: formatPhoneNumber(sms.smsFrom),
          SmsTo: formatPhoneNumber(sms.smsTo),
          SmsText: sms.messageText || '',
        }))
      : [];

  const smsDetailsColumns = [
    {
      field: 'date',
      sortKey: 'createdAt',
      headerName: 'Date',
      width: 250,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: true,
    },
    {
      field: 'SmsFrom',
      sortKey: 'smsFrom',
      headerName: 'SMS From',
      width: 200,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: true,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography>{params.row.SmsFrom || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'SmsTo',
      sortKey: 'smsTo',
      headerName: 'SMS To',
      width: 200,
      flex: isMediumScreen ? 1.2 : 0,
      sortable: true,
      renderCell: (params) => {
        return (
          <Box className="callFromWrapper">
            <Typography>{params.row.SmsTo || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'SmsText',
      sortKey: 'messageText',
      headerName: 'SMS Text',
      width: 300,
      flex: isMediumScreen ? 2 : 0,
      sortable: true,
      renderCell: (params) => {
        return (
          <Box className="textMessageWrapper">
            <Tooltip
              title={params.row.SmsText}
              placement="bottom"
              disableHoverListener={!params.row.SmsText || params.row.SmsText.length < 30}
            >
              <Typography>{params.row.SmsText || '-'}</Typography>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleHistoryChangePage = (event, newPage) => {
    if (isSmsHistory) {
      dispatch(
        fetchSmsTrackingHistory({
          id: trackingId,
          pagination: { page: newPage, pageSize: smsHistoryPerPage },
          ...sort,
        })
      );
    } else {
      dispatch(
        fetchPhoneTrackingHistory({
          id: trackingId,
          pagination: { page: newPage, pageSize: historyPerPage },
          ...sort,
        })
      );
    }
  };

  const handleHistoryChangePageSize = (pageSize) => {
    if (isSmsHistory) {
      dispatch(
        fetchSmsTrackingHistory({ id: trackingId, pagination: { page: 1, pageSize }, ...sort })
      );
    } else {
      dispatch(
        fetchPhoneTrackingHistory({ id: trackingId, pagination: { page: 1, pageSize }, ...sort })
      );
    }
  };

  // A new history sort goes to the URL and the table reloads from page 1 with it (same page size).
  const handleHistorySortChange = (nextSort) => {
    updatePhoneTrackingUrl({ sort: nextSort });

    if (isSmsHistory) {
      dispatch(
        fetchSmsTrackingHistory({
          id: trackingId,
          pagination: { page: 1, pageSize: smsHistoryPerPage },
          ...nextSort,
        })
      );
    } else {
      dispatch(
        fetchPhoneTrackingHistory({
          id: trackingId,
          pagination: { page: 1, pageSize: historyPerPage },
          ...nextSort,
        })
      );
    }
  };

  // URL effect: runs on mount and whenever search or sort change in the URL (typing, clearing,
  // back/forward, shared link). It restores the input, goes back to page 1 and fetches once.
  // The history views have no list query of their own, but they still need the list's totals, so
  // they only trigger a fetch if the list was never loaded (e.g. a direct visit to a history URL).
  useEffect(() => {
    setSearch(parsedQuery.search);

    if (showHistory && hasLoadedListRef.current) {
      return;
    }

    hasLoadedListRef.current = true;
    // On a direct visit to a history URL the query holds the history sort, which the list API
    // would reject: the list is then only loaded for its totals, with its default order.
    dispatch(
      fetchPhoneTrackingNumbers({
        pagination: { page: 1, pageSize: perPageRef.current },
        ...(showHistory ? {} : { search: parsedQuery.search, ...parsedQuery.sort }),
      })
    );
  }, [dispatch, parsedQuery, queryString, showHistory]);

  useEffect(() => {
    dispatch(getAllPaymentMethods());
  }, [dispatch]);

  // Handle payment method added from PhoneTracking context
  const handlePaymentMethodAdded = () => {
    if (wasPaymentMethodModalOpen) {
      setWasPaymentMethodModalOpen(false);
      setModalOpen({ type: 'activate', open: true });
      setSelectedRow(null);
    }
  };

  const activeHistoryRows = isSmsHistory ? smsDetailsRows : numberDetailsRows;
  const activeHistoryColumns = isSmsHistory ? smsDetailsColumns : numberDetailsColumns;
  const { sortProps: historySortProps } = useSortParams(
    activeHistoryColumns,
    handleHistorySortChange,
    sort
  );
  const activeHistoryReduxLoader = isSmsHistory ? smsHistoryLoader : historyLoader;
  const activeHistoryLoader =
    loadedHistoryRequestKey !== historyRequestKey || activeHistoryReduxLoader;
  const activeHistoryCount = isSmsHistory ? smsHistoryCount : historyCount;
  const activeHistoryLastPage = isSmsHistory ? smsHistoryLastPage : historyLastPage;
  const activeHistoryCurrentPage = isSmsHistory ? smsHistoryCurrentPage : historyCurrentPage;
  const activeHistoryPerPage = isSmsHistory ? smsHistoryPerPage : historyPerPage;
  const activeHistoryFrom = isSmsHistory ? smsHistoryFrom : historyFrom;
  const activeHistoryTo = isSmsHistory ? smsHistoryTo : historyTo;

  const handleOpenActivateTrackingModal = () => {
    if (!isTestMode && paymentMethods.length === 0) {
      setPaymentMethodModal(true);
      setWasPaymentMethodModalOpen(true);
      return;
    }

    setModalOpen({ type: 'activate', open: true });
    setSelectedRow(null);
  };

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <ActivateTrackingModal
        modalOpen={modalOpen}
        isCompanyEnabled={isCompanyEnabled}
        onClose={() => {
          setModalOpen((prev) => ({
            ...prev,
            open: false,
          }));
          setSelectedRow(null);
        }}
        editingId={selectedRow?.id}
        onSuccess={() => {
          // Refresh the list after successful create/update
          loadPhoneNumbers();
        }}
      />
      <ConfirmModal openConfirmModal={openConfirmModal} />
      <LinkedTemplatesModal
        open={linkedTemplatesModal.open}
        templates={linkedTemplatesModal.templates}
        onClose={() => setLinkedTemplatesModal({ open: false, templates: [] })}
      />
      <Stripe>
        <PaymentMethod
          open={paymentMethodModal}
          handleClose={() => {
            setPaymentMethodModal(false);
          }}
          handleExport={() => {}}
          message=""
          header=""
          onSuccessMethod={handlePaymentMethodAdded}
        />
      </Stripe>

      {+totalRecordsInDB <= 0 ? (
        <NoTrackingScreen
          addNewTracking={() => {
            handleOpenActivateTrackingModal();
          }}
        />
      ) : (
        <Box className={`phoneTrackingWrapper ${!showHistory ? 'phoneTrackingListPage' : ''}`}>
          <Box className={`phoneTrackingCard ${!showHistory ? 'phoneTrackingListCard' : ''}`}>
            <Box className={`phoneTrackingHeader ${!showHistory ? 'olcHeaderWrap' : ''}`}>
              {showHistory ? (
                <OlcPageBackButton
                  onClick={() =>
                    navigate({
                      pathname: '/phone-tracking',
                      search: location.state?.listQuery ? `?${location.state.listQuery}` : '',
                    })
                  }
                >
                  Back to Tracking Phone Numbers
                </OlcPageBackButton>
              ) : (
                <Typography variant="h6" className="phoneTrackingCardTitle pageTitle">
                  Tracking Phone Numbers
                </Typography>
              )}
              {!showHistory && (
                <Box className="pageNewBtn">
                  <OlcButton
                    variant="primary"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      handleOpenActivateTrackingModal();
                    }}
                  >
                    Add New
                  </OlcButton>
                </Box>
              )}
            </Box>
            <Box className="phoneTrackingContent">
              {!showHistory && (
                <Box className="olcTableToolbar phoneTrackingToolbar">
                  <Box
                    className="searchWrapper olcSearchWrapper"
                    sx={{
                      maxWidth: 'var(--olc-search-width) !important',
                    }}
                  >
                    <Input
                      className={`searchInput ${searchApplied ? 'searchApplied' : ''}`}
                      type="text"
                      value={search}
                      onKeyDown={debouncedHandleKeyDown}
                      onChange={handleSearchChange}
                      placeholder="Search by name or call forwarding number"
                      title="Search by name or call forwarding number"
                      endAdornment={
                        <InputAdornment position="end" className="searchInputAdornment">
                          <IconButton
                            aria-label="toggle password visibility"
                            edge="end"
                            className="searchIcon"
                            onClick={handleSearch}
                            focusRipple={false}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'transparent',
                              },
                            }}
                          >
                            <img src={ContactSearchCIcon} alt="search" />
                          </IconButton>
                          {search?.trim() && (
                            <IconButton
                              className="cpClearInputBtn"
                              size="small"
                              onClick={clearSearch}
                              edge="end"
                              sx={{ padding: '4px' }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </InputAdornment>
                      }
                    />
                  </Box>
                </Box>
              )}
              {!showHistory ? (
                <>
                  <Box
                    className={`olcTableGridInternal phoneTrackingTableGrid ${
                      loading ? 'table-loading' : ''
                    } ${phoneTrackingNumbers.length === 0 ? 'table-empty' : ''}`}
                  >
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      shrinkColumnsToFit
                      loading={loading}
                      disableColumnMenu
                      disableColumnFilter
                      disableColumnSelector
                      {...sortProps}
                      rowSelection={false}
                      columnHeaderHeight={47}
                      rowHeight={61}
                      slots={{
                        noRowsOverlay: () => (
                          <Typography className="noResultsText">No Results Found</Typography>
                        ),
                        noResultsOverlay: () => (
                          <Typography className="noResultsText">No Results Found</Typography>
                        ),
                      }}
                      getRowClassName={(params) => {
                        return params.row.Status === 'Released' || params.row.status === 'Released'
                          ? 'releasedRow'
                          : '';
                      }}
                      classes={{
                        ...classes,
                        virtualScroller: `phoneTrackingVirtualScroller ${phoneTrackingNumbers.length === 0 ? 'noResults' : ''}`,
                      }}
                      sx={{
                        '& .MuiDataGrid-row:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          cursor: 'pointer',
                        },
                      }}
                    />
                  </Box>
                  {(loading || totalCount > 0) && (
                    <OlcTablePagination
                      count={lastPage || 1}
                      page={currentPage || 1}
                      pageSize={perPage}
                      pageSizes={pageSizes}
                      onPageChange={handleChangePage}
                      onPageSizeChange={(event) => {
                        loadPhoneNumbers(1, event.target.value);
                      }}
                      from={from}
                      to={to}
                      total={totalCount}
                      loading={loading}
                    />
                  )}
                </>
              ) : (
                <>
                  {!activeHistoryLoader && activeHistoryRows.length === 0 ? (
                    <Box>
                      <NoRecords
                        icon={
                          <img
                            src={CustomFieldIcon}
                            style={{ height: '33px', width: '33px' }}
                            alt="no-records"
                          />
                        }
                        title={isSmsHistory ? 'No SMS History' : 'No Call History'}
                        message={
                          isSmsHistory ? (
                            <>
                              <p style={{ margin: '0px' }}>No SMS history available yet.</p>
                              <p style={{ margin: '0px' }}>
                                SMS messages will appear here once they are received.
                              </p>
                            </>
                          ) : (
                            <>
                              <p style={{ margin: '0px' }}>No call history available yet.</p>
                              <p style={{ margin: '0px' }}>
                                Calls will appear here once they are received.
                              </p>
                            </>
                          )
                        }
                      />
                    </Box>
                  ) : (
                    <>
                      <Box
                        className={`olcTableGridInternal phoneTrackingTableGrid ${activeHistoryLoader ? 'table-loading' : ''} ${activeHistoryRows.length === 0 ? 'table-empty' : ''}`}
                      >
                        <DataGrid
                          rows={activeHistoryLoader ? [] : activeHistoryRows}
                          columns={activeHistoryColumns}
                          loading={activeHistoryLoader}
                          disableColumnMenu
                          disableColumnFilter
                          disableColumnSelector
                          {...historySortProps}
                          rowSelection={false}
                          columnHeaderHeight={47}
                          rowHeight={61}
                          classes={classes}
                        />
                      </Box>
                      {(activeHistoryLoader || activeHistoryCount > 0) && (
                        <OlcTablePagination
                          count={activeHistoryLastPage || 1}
                          page={activeHistoryCurrentPage || 1}
                          pageSize={activeHistoryPerPage}
                          pageSizes={pageSizes}
                          onPageChange={handleHistoryChangePage}
                          onPageSizeChange={(event) =>
                            handleHistoryChangePageSize(event.target.value)
                          }
                          from={activeHistoryFrom}
                          to={activeHistoryTo}
                          total={activeHistoryCount}
                          loading={activeHistoryLoader}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
          <OlcActionMenu
            id="phone-tracking-actions-menu"
            anchorEl={anchorEl}
            labelledBy={selectedRow ? `phone-tracking-actions-${selectedRow.id}` : undefined}
            onClose={() => {
              setAnchorEl(null);
            }}
            actions={
              selectedRow
                ? [
                    {
                      value: 'call-history',
                      icon: HistoryCIcon,
                      label: 'View Call History',
                      onClick: () => handleViewHistory(selectedRow.id, 'call'),
                    },
                    {
                      value: 'sms-history',
                      icon: HistoryCIcon,
                      label: 'View SMS History',
                      onClick: () => handleViewHistory(selectedRow.id, 'sms'),
                    },
                    {
                      value: 'edit',
                      icon: EditPhoneCIcon,
                      label: 'Edit',
                      hidden: selectedRow.status !== 'Active',
                      onClick: () => setModalOpen({ type: 'edit', open: true }),
                    },
                    {
                      value: 'release',
                      icon: ReleasePhoneCIcon,
                      label: 'Release Phone Number',
                      hidden: selectedRow.status !== 'Active',
                      onClick: handleOpenConfirmModal,
                    },
                  ]
                : []
            }
          />
        </Box>
      )}
    </>
  );
};

export default PhoneTracking;
